import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { GiftBoxItem } from '../pages/gifts/gifts';
import { Product } from '../models/product';

@Injectable({
  providedIn: 'root',
})
export class GiftBoxService {
  private packedItemsSubject = new BehaviorSubject<GiftBoxItem[]>(this.loadFromStorage());
  packedItems$: Observable<GiftBoxItem[]> = this.packedItemsSubject.asObservable();

  constructor() {}

  get currentItems(): GiftBoxItem[] {
    return this.packedItemsSubject.value;
  }

  private loadFromStorage(): GiftBoxItem[] {
    try {
      const saved = localStorage.getItem('hela_gift_box_items');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  }

  private saveToStorage(items: GiftBoxItem[]): void {
    try {
      localStorage.setItem('hela_gift_box_items', JSON.stringify(items));
    } catch {}
  }

  /**
   * Add a product or gift item to the Gift Box
   */
  addItem(item: GiftBoxItem | Product, quantity: number = 1): void {
    const current = [...this.currentItems];
    const itemId = 'id' in item && item.id ? String(item.id) : `p-${Date.now()}`;
    const itemName = item.name;
    const itemPrice = item.price;
    const itemImage =
      'images' in item && (item as Product).images?.length
        ? (item as Product).images[0]
        : 'image' in item
          ? (item as GiftBoxItem).image
          : undefined;

    const existingIndex = current.findIndex((i) => i.id === itemId || i.name === itemName);

    if (existingIndex > -1) {
      current[existingIndex].quantity += quantity;
    } else {
      const isCustomDesign = itemId.startsWith('custom') || ('category' in item && (item as GiftBoxItem).category === 'user_design');
      current.push({
        id: itemId,
        name: itemName,
        price: itemPrice,
        category: isCustomDesign ? 'user_design' : 'preset',
        image: itemImage,
        quantity: quantity,
        productType: 'productType' in item ? (item as GiftBoxItem).productType : undefined,
        colorCode: 'colorCode' in item ? (item as GiftBoxItem).colorCode : undefined,
      });
    }

    this.packedItemsSubject.next(current);
    this.saveToStorage(current);
  }

  /**
   * Remove item from Gift Box
   */
  removeItem(itemId: string): void {
    const updated = this.currentItems.filter((i) => i.id !== itemId);
    this.packedItemsSubject.next(updated);
    this.saveToStorage(updated);
  }

  /**
   * Update quantity of a packed item
   */
  updateQuantity(itemId: string, delta: number): void {
    const current = [...this.currentItems];
    const item = current.find((i) => i.id === itemId);
    if (item) {
      item.quantity += delta;
      if (item.quantity <= 0) {
        this.removeItem(itemId);
      } else {
        this.packedItemsSubject.next(current);
        this.saveToStorage(current);
      }
    }
  }

  /**
   * Clear all packed items
   */
  clearBox(): void {
    this.packedItemsSubject.next([]);
    this.saveToStorage([]);
  }

  /**
   * Set packed items directly
   */
  setPackedItems(items: GiftBoxItem[]): void {
    this.packedItemsSubject.next(items);
    this.saveToStorage(items);
  }
}
